"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import Image from "next/image";
import { Heart, Sparkle } from "lucide-react";
import { motion, useMotionTemplate, useMotionValue, useReducedMotion, useSpring, useTransform, type MotionValue } from "motion/react";
import { cn } from "@/lib/utils";
const PHOTO_BASE_URL = "https://kycacontent.s3.eu-central-1.amazonaws.com/guestgallery/landing-page";

// Landing-page wedding photos, chosen without close-up guests or children.
// Each lands on its own path and drifts on its own rhythm, so the collage
// never moves in lockstep.
const POLAROIDS = [{
  src: `${PHOTO_BASE_URL}/1.webp`,
  aspect: "aspect-[4/5]",
  position: "left-3 top-1 w-[25%] md:-left-6 md:top-2 md:w-[30%] lg:-left-14",
  tilt: "-rotate-[9deg]",
  depth: 1.3,
  land: {
    x: "70%",
    y: "60%",
    rotate: "14deg",
    delay: 450,
    duration: 820
  },
  drift: {
    x: "2px",
    y: "-7px",
    rotate: "1.5deg",
    duration: 6.2,
    delay: 1.4
  }
}, {
  src: `${PHOTO_BASE_URL}/4.webp`,
  aspect: "aspect-[5/4]",
  position: "left-3 bottom-[14%] w-[30%] md:-left-8 md:bottom-[14%] md:w-[36%] lg:-left-20",
  tilt: "rotate-[6deg]",
  depth: 0.8,
  land: {
    x: "75%",
    y: "-30%",
    rotate: "-10deg",
    delay: 720,
    duration: 900
  },
  drift: {
    x: "-3px",
    y: "-5px",
    rotate: "-1deg",
    duration: 7.4,
    delay: 1.9
  }
}, {
  src: `${PHOTO_BASE_URL}/3.webp`,
  aspect: "aspect-[4/5]",
  position: "right-3 top-[7%] w-[23%] md:right-0 md:top-0 md:w-[27%] lg:-right-12",
  tilt: "rotate-[8deg]",
  depth: 1.6,
  land: {
    x: "-80%",
    y: "70%",
    rotate: "-16deg",
    delay: 980,
    duration: 760
  },
  drift: {
    x: "2px",
    y: "-8px",
    rotate: "-1.5deg",
    duration: 5.6,
    delay: 2.1
  }
}, {
  src: `${PHOTO_BASE_URL}/6.webp`,
  aspect: "aspect-[4/5]",
  position: "right-3 bottom-0 w-[25%] md:-right-2 md:-bottom-4 md:w-[29%] lg:-right-16",
  tilt: "-rotate-[5deg]",
  depth: 1.1,
  land: {
    x: "-70%",
    y: "-55%",
    rotate: "12deg",
    delay: 1240,
    duration: 860
  },
  drift: {
    x: "-2px",
    y: "-6px",
    rotate: "1deg",
    duration: 6.8,
    delay: 2.6
  }
}] as const;
const PARALLAX_RANGE_PX = 12;
interface ParallaxLayerProps {
  pointerX: MotionValue<number>;
  pointerY: MotionValue<number>;
  depth: number;
  className?: string;
  children: React.ReactNode;
}
function ParallaxLayer({
  pointerX,
  pointerY,
  depth,
  className,
  children
}: ParallaxLayerProps): React.ReactElement {
  const x = useTransform(pointerX, value => value * depth * PARALLAX_RANGE_PX);
  const y = useTransform(pointerY, value_0 => value_0 * depth * PARALLAX_RANGE_PX);
  const transform = useMotionTemplate`translate3d(${x}px, ${y}px, 0)`;
  return <motion.div className={className} style={{
    transform
  }}>
      {children}
    </motion.div>;
}
interface HeroCollageProps {
  imageAlt: string;
}

/**
 * The hero picture: a guest scanning a QR card, with guest photos flying out
 * of the phone and landing around it. On desktop the layers follow the
 * pointer at different depths.
 */
export default function HeroCollage(t0) {
  const $ = _c(27);
  const {
    imageAlt
  } = t0;
  const containerRef = React.useRef(null);
  const shouldReduceMotion = useReducedMotion();
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      mass: 1,
      stiffness: 100,
      damping: 10
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const pointerX = useSpring(rawX, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      mass: 1,
      stiffness: 100,
      damping: 10
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const pointerY = useSpring(rawY, t2);
  let t3;
  let t4;
  if ($[2] !== rawX || $[3] !== rawY || $[4] !== shouldReduceMotion) {
    t3 = () => {
      const container = containerRef.current;
      const hero = container?.closest("[data-hero]");
      if (!container || !hero || shouldReduceMotion) {
        return;
      }
      if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
        return;
      }
      const handlePointerMove = event => {
        if (event.pointerType !== "mouse") {
          return;
        }
        const rect = container.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const clamp = _temp;
        rawX.set(clamp((event.clientX - centerX) / (window.innerWidth / 2)));
        rawY.set(clamp((event.clientY - centerY) / (window.innerHeight / 2)));
      };
      const handlePointerLeave = () => {
        rawX.set(0);
        rawY.set(0);
      };
      hero.addEventListener("pointermove", handlePointerMove);
      hero.addEventListener("pointerleave", handlePointerLeave);
      return () => {
        hero.removeEventListener("pointermove", handlePointerMove);
        hero.removeEventListener("pointerleave", handlePointerLeave);
      };
    };
    t4 = [rawX, rawY, shouldReduceMotion];
    $[2] = rawX;
    $[3] = rawY;
    $[4] = shouldReduceMotion;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  React.useEffect(t3, t4);
  let t5;
  if ($[7] !== imageAlt) {
    t5 = <div className="hero-card"><div className="rotate-3 rounded-3xl bg-white p-2 shadow-2xl transition-transform duration-500 ease-[var(--ease-out-strong)] can-hover:hover:rotate-0"><img alt={imageAlt} className="mx-auto w-full rounded-2xl object-scale-down" fetchPriority="high" height={662} src={`${PHOTO_BASE_URL}/scanqr.webp`} width={679} /></div></div>;
    $[7] = imageAlt;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== pointerX || $[10] !== pointerY || $[11] !== t5) {
    t6 = <ParallaxLayer pointerX={pointerX} pointerY={pointerY} depth={0.45}>{t5}</ParallaxLayer>;
    $[9] = pointerX;
    $[10] = pointerY;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== pointerX || $[14] !== pointerY) {
    t7 = POLAROIDS.map(photo => <ParallaxLayer key={photo.src} pointerX={pointerX} pointerY={pointerY} depth={photo.depth} className={cn("absolute z-10", photo.position)}><div className="hero-polaroid" style={{
        "--land-x": photo.land.x,
        "--land-y": photo.land.y,
        "--land-rotate": photo.land.rotate,
        "--land-delay": `${photo.land.delay}ms`,
        "--land-duration": `${photo.land.duration}ms`
      } as React.CSSProperties}><div className="hero-drift" style={{
          "--drift-x": photo.drift.x,
          "--drift-y": photo.drift.y,
          "--drift-rotate": photo.drift.rotate,
          "--drift-duration": `${photo.drift.duration}s`,
          "--drift-delay": `${photo.drift.delay}s`
        } as React.CSSProperties}><div className={cn("rounded-md bg-white p-1 pb-4 shadow-[0_18px_40px_-18px_rgba(58,44,36,0.55)] transition-transform duration-500 ease-[var(--ease-overshoot)] can-hover:hover:rotate-0 can-hover:hover:scale-110 md:p-1.5 md:pb-6", photo.tilt)}><div className={cn("relative overflow-hidden rounded-sm bg-gg-orange-100", photo.aspect)}><Image alt="" className="object-cover" fill={true} loading="eager" sizes="(min-width: 768px) 144px, 120px" src={photo.src} /></div></div></div></div></ParallaxLayer>);
    $[13] = pointerX;
    $[14] = pointerY;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span aria-hidden="true" className="absolute bottom-[18%] left-1/2 grid h-11 w-11 -translate-x-1/2 animate-upload-pop place-items-center rounded-full bg-white shadow-lg [animation-delay:2000ms] motion-reduce:animate-none"><Heart className="h-5 w-5 fill-primary text-primary" /></span>;
    t9 = <Sparkle aria-hidden="true" className="hero-twinkle absolute right-[18%] top-[3%] h-6 w-6 fill-gg-orange-300 text-gg-orange-500" />;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      "--twinkle-duration": "3.3s",
      "--twinkle-delay": "0.8s"
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Sparkle aria-hidden="true" className="hero-twinkle absolute bottom-[4%] left-[22%] h-4 w-4 fill-gg-orange-300 text-gg-orange-500" style={t10 as React.CSSProperties} />;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== pointerX || $[21] !== pointerY) {
    t12 = <ParallaxLayer pointerX={pointerX} pointerY={pointerY} depth={2} className="pointer-events-none absolute inset-0 z-20">{t8}{t9}{t11}</ParallaxLayer>;
    $[20] = pointerX;
    $[21] = pointerY;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t6 || $[25] !== t7) {
    t13 = <div ref={containerRef} className="relative mx-auto w-full max-w-[24rem] px-[15%] py-8 md:max-w-sm md:px-0">{t6}{t7}{t12}</div>;
    $[23] = t12;
    $[24] = t6;
    $[25] = t7;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  return t13;
}
function _temp(value) {
  return Math.max(-1, Math.min(1, value));
}